// Shaped specialist roster for pool and routing tests (spec §7). Block 7 pins
// this fixture to seed.sql via a drift guard. NY and TX stand in for the spec's
// arbitrary S1/S2 placeholders — real US state codes matching usStateCodeSchema.

import {
  CANONICAL_CARE_CALL_TYPE_DURATIONS,
  type CareCallTypePolicy,
  type CareSpecialistCoverage,
  type CareSpecialistLicenceRead,
  type RoutableCareCallType,
} from '../care-call-types';

/** Licence states used by the shaped roster (NY / TX per usStateCodeSchema). */
export const FIXTURE_LICENCE_STATES = {
  s1: 'NY',
  s2: 'TX',
} as const;

export type FixtureCounsellorKey =
  | 'counsellor-a'
  | 'counsellor-b'
  | 'counsellor-c'
  | 'counsellor-d'
  | 'prescriber-e'
  | 'specialist-f';

/** Dev/preview display_name values in seed.sql for unlocked shaped specialists. */
export const SEED_DISPLAY_NAME_BY_FIXTURE: Pick<
  Record<FixtureCounsellorKey, string>,
  'counsellor-d' | 'prescriber-e' | 'specialist-f'
> = {
  'counsellor-d': 'Counsellor D — narrow window (2h) [Dev]',
  'prescriber-e': 'Prescriber E — provider call (AUD+OUD NY) [Dev]',
  'specialist-f': 'Specialist F — inactive (never surfaces) [Dev]',
};

/** Maps fixture keys to seed.sql counsellor_key values (ADR-121 Rule 3). */
export const SEED_COUNSELLOR_KEY_BY_FIXTURE: Record<FixtureCounsellorKey, string> = {
  'counsellor-a': 'qa-counsellor-dev',
  'counsellor-b': 'qa-counsellor-dev-2',
  'counsellor-c': 'qa-counsellor-dev-3',
  'counsellor-d': 'counsellor-d-dev',
  'prescriber-e': 'prescriber-e-dev',
  'specialist-f': 'specialist-f-dev',
};

export type FixtureSpecialist = {
  readonly key: FixtureCounsellorKey;
  readonly displayName: string;
  readonly clinicalRole: 'care_specialist' | 'counselor' | 'prescriber' | 'physician';
  readonly active: boolean;
  /**
   * Receives newly created members. Independent of coverage: a default-pool
   * specialist keeps hosting whatever calls their coverage allows.
   */
  readonly defaultPool: boolean;
  readonly coverage: readonly CareSpecialistCoverage[];
  readonly licences: readonly CareSpecialistLicenceRead[];
  /** When set, only this daily window is bookable (Counsellor D). */
  readonly narrowAvailabilityHours?: { startHour: number; endHour: number };
};

function coverage(callType: RoutableCareCallType, enabled: boolean): CareSpecialistCoverage {
  return { callType, enabled };
}

function licence(
  state: string,
  status: 'active' | 'inactive',
  audReady: boolean,
  oudReady: boolean,
  expiresAt: string | null = null,
): CareSpecialistLicenceRead {
  return { state, status, audReady, oudReady, expiresAt, licenceLevel: null };
}

/** Per-call-type policy defaults aligned with seeded `care_call_type_policies`. */
export function fixtureCallTypePolicy(
  callType: RoutableCareCallType,
): Omit<CareCallTypePolicy, 'callType'> {
  return {
    durationMinutes: CANONICAL_CARE_CALL_TYPE_DURATIONS[callType],
    highAcuityDurationMinutes: callType === 'welcome_call_substance' ? 60 : null,
    minNoticeMinutes: 0,
    horizonDays: 14,
    cancellationNoticeMinutes: 1440,
    healthieContactType: 'Healthie Video Call',
    healthieAppointmentTypeId: null,
  };
}

/** Shorthand for welcome_call_substance pool tests. */
export const FIXTURE_CALL_TYPE_POLICY_DEFAULTS = fixtureCallTypePolicy('welcome_call_substance');

export const SHAPED_CARE_ROSTER: readonly FixtureSpecialist[] = [
  {
    key: 'counsellor-a',
    displayName: 'Counsellor A',
    clinicalRole: 'counselor',
    active: true,
    defaultPool: false,
    coverage: [
      coverage('welcome_call_substance', true),
      coverage('welcome_call_tobacco', true),
      coverage('welcome_call_behavioral', true),
      coverage('welcome_call_low_no', true),
      coverage('treatment_plan', true),
      coverage('progress_purpose_checkin', true),
    ],
    licences: [
      licence(FIXTURE_LICENCE_STATES.s1, 'active', true, true),
      licence(FIXTURE_LICENCE_STATES.s2, 'active', true, true),
    ],
  },
  {
    key: 'counsellor-b',
    displayName: 'Counsellor B',
    clinicalRole: 'counselor',
    active: true,
    defaultPool: true,
    coverage: [coverage('welcome_call_substance', true), coverage('treatment_plan', true)],
    licences: [
      licence(FIXTURE_LICENCE_STATES.s1, 'inactive', true, false),
      licence(FIXTURE_LICENCE_STATES.s2, 'active', true, false),
    ],
  },
  {
    key: 'counsellor-c',
    displayName: 'Counsellor C',
    clinicalRole: 'counselor',
    active: true,
    defaultPool: true,
    coverage: [coverage('welcome_call_tobacco', true), coverage('welcome_call_behavioral', true)],
    licences: [],
  },
  {
    key: 'counsellor-d',
    displayName: 'Counsellor D',
    clinicalRole: 'care_specialist',
    active: true,
    defaultPool: false,
    coverage: [coverage('treatment_plan', true), coverage('progress_purpose_checkin', true)],
    licences: [
      licence(FIXTURE_LICENCE_STATES.s1, 'active', true, true),
      licence(FIXTURE_LICENCE_STATES.s2, 'active', true, true),
    ],
    narrowAvailabilityHours: { startHour: 10, endHour: 12 },
  },
  {
    key: 'prescriber-e',
    displayName: 'Prescriber E',
    clinicalRole: 'prescriber',
    active: true,
    defaultPool: false,
    coverage: [coverage('provider_call', true)],
    licences: [licence(FIXTURE_LICENCE_STATES.s1, 'active', true, true)],
  },
  {
    key: 'specialist-f',
    displayName: 'Specialist F',
    clinicalRole: 'care_specialist',
    active: false,
    defaultPool: false,
    coverage: [
      coverage('welcome_call_substance', true),
      coverage('welcome_call_tobacco', true),
      coverage('welcome_call_behavioral', true),
      coverage('treatment_plan', true),
      coverage('provider_call', true),
    ],
    licences: [
      licence(FIXTURE_LICENCE_STATES.s1, 'active', true, true),
      licence(FIXTURE_LICENCE_STATES.s2, 'active', true, true),
    ],
  },
];
